<template>
  <VueFinalModal
    :modal-id="name"
    v-model="isOpen"
    :displayDirective="displayDirective"
    :teleportTo="teleportTo"
    :hideOverlay="hideOverlay"
    :overlayTransition="overlayTransition"
    :contentTransition="contentTransition"
    :overlayClass="overlayClass"
    :contentClass="contentClass"
    :overlayStyle="overlayStyle"
    :contentStyle="contentStyleMerged"
    :clickToClose="clickToClose"
    :escToClose="escToClose"
    :lockScroll="lockScroll"
    :background="background"
    @before-open="handleBeforeOpen"
    @before-close="handleBeforeClose"
    @opened="handleOpened"
    @closed="handleClosed"
    @click-outside="handleClickOutside"
    :class="['vfm__container', wrapperClass, attrs?.class || ''].join(' ')"
  >
    <div v-if="title.length > 0" class="flex justify-between items-center pb-4 text-left">
      <p class="flex-2 text-2xl font-bold">{{ title }}</p>
    </div>
    <CloseIcon v-if="showCloseButton" class="flex absolute top-4 right-4 z-10 bg-white p-2 border" @click="closeModal" />
    <slot></slot>
  </VueFinalModal>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUpdated, onBeforeUnmount, useAttrs } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import CloseIcon from '@/components/CloseIcon.vue'
import $modal from '@/components/_globalModals'
import { useStore } from 'vuex'

// props
const props = defineProps({
  name: { type: String, required: true },
  title: { type: String, default: '' },
  showCloseButton: { type: Boolean, default: false },
  teleportTo: { type: [String, null, Boolean, Object], default: () => null },
  displayDirective: { type: String, default: 'if' },
  hideOverlay: { type: Boolean, default: false },
  overlayTransition: { type: String, default: 'vfm-fade' },
  contentTransition: { type: String, default: 'vfm-fade' },
  overlayClass: { type: [Object, Array, String], default: () => '' },
  classes: { type: [Array, String], default: () => '' },
  overlayStyle: { type: [String, Array, Object], default: () => {} },
  contentStyle: { type: [String, Array, Object], default: () => {} },
  clickToClose: { type: Boolean, default: true },
  escToClose: { type: Boolean, default: true },
  lockScroll: { type: Boolean, default: true },
  background: { type: String, default: 'non-interactive' },
  adaptive: { type: Boolean, default: false },
  scrollable: { type: Boolean, default: false },
  width: { type: [String, Number], default: 600 },
  height: { type: [String, Number], default: 300 },
  minWidth: { type: Number, default: 0 },
  maxWidth: { type: Number, default: 0 },
  minHeight: { type: Number, default: 0 },
  maxHeight: { type: Number, default: 0 }
})

// emits
const emit = defineEmits(['before-open', 'before-close', 'opened', 'closed', 'click-outside'])

// attrs and store
const attrs = useAttrs()
const store = useStore()

// data
const isOpen = ref(false)
const params = ref(null)
const fitFullHeight = ref(false)
let resizeTimer = null

// computed
const contentStyleMerged = computed(() => {
  let contentStyleMerged = {}
  if (props.width) {
    let width = props.width
    if (typeof props.width === 'number' || (typeof props.width === 'string' && props.width.match(/^\d+$/))) {
      width = `${props.width}px`
    }
    contentStyleMerged.width = width
  }
  if (props.height) {
    let height = props.height
    if (typeof props.height === 'number' || (typeof props.height === 'string' && props.height.match(/^\d+$/))) {
      height = `${props.height}px`
    }
    contentStyleMerged.height = height
  }
  if (props.minWidth) {
    let minWidth = props.minWidth
    if (typeof props.minWidth === 'number' || (typeof props.minWidth === 'string' && props.minWidth.match(/^\d+$/))) {
      minWidth = `${props.minWidth}px`
    }
    contentStyleMerged.minWidth = minWidth
  }
  if (props.maxWidth) {
    let maxWidth = props.maxWidth
    if (typeof props.maxWidth === 'number' || (typeof props.maxWidth === 'string' && props.maxWidth.match(/^\d+$/))) {
      maxWidth = `${props.maxWidth}px`
    }
    contentStyleMerged.maxWidth = maxWidth
  }
  if (props.minHeight) {
    let minHeight = props.minHeight
    if (typeof props.minHeight === 'number' || (typeof props.minHeight === 'string' && props.minHeight.match(/^\d+$/))) {
      minHeight = `${props.minHeight}px`
    }
    contentStyleMerged.minHeight = minHeight
  }
  if (props.maxHeight) {
    let maxHeight = props.maxHeight
    if (typeof props.maxHeight === 'number' || (typeof props.maxHeight === 'string' && props.maxHeight.match(/^\d+$/))) {
      maxHeight = `${props.maxHeight}px`
    }
    contentStyleMerged.maxHeight = maxHeight
  }
  return contentStyleMerged
})

const contentClass = computed(() => {
  let newClasses = 'bg-white '
  let classes = ''
  if (!Array.isArray(classes)) {
    classes = props.classes.trim().split(' ')
  } else {
    classes = props.classes.map((item) => item.trim())
  }
  if (classes.length > 0) {
    classes.forEach((cls) => {
      newClasses += `${cls} `
    })
  }
  if (props.scrollable) {
    newClasses += 'overflow-auto '
  }
  if (props.adaptive) {
    newClasses += 'max-w-full  ' // max-h-full
  }
  if (props.showCloseButton) {
    newClasses += 'relative '
  }

  return newClasses.trim()
})

const wrapperClass = computed(() => {
  let newClasses = `vfm__${props.name} relative flex flex-col items-center `

  if (fitFullHeight.value) {
    newClasses += 'justify-start '
  } else {
    newClasses += 'justify-center '
  }
  if (props.scrollable) {
    newClasses += 'overflow-auto '
  }

  return newClasses.trim()
})

// watch
watch(isOpen, (value) => {
  if (props.lockScroll) {
    if (value) {
      document.getElementsByTagName('html')[0].style.overflow = 'hidden'
      resizeTimer = setInterval(async () => {
        handleResizer()
      }, 300)
    } else {
      document.getElementsByTagName('html')[0].style.overflow = ''
      if (resizeTimer) {
        clearInterval(resizeTimer)
      }
    }
  }
})

// hooks
onMounted(() => {
  $modal.register(props.name)
  watchModalStore()
})

onUpdated(() => {
  syncModalStore()
})

onBeforeUnmount(() => {
  if (resizeTimer) {
    clearInterval(resizeTimer)
  }
})

// methods
const handleBeforeOpen = (event) => {
  event.cancel = event?.stop ? event.stop : () => {}
  event.params = params.value
  emit('before-open', event)
}

const handleBeforeClose = (event) => {
  event.cancel = event?.stop ? event.stop : () => {}
  event.params = params.value
  emit('before-close', event)
}

const handleOpened = () => {
  emit('opened')
}
const handleClosed = () => {
  emit('closed')
}
const handleClickOutside = () => {
  emit('click-outside')
}
const closeModal = () => {
  $modal.hide(props.name)
}

const syncModalStore = () => {
  const storeIsOpen = store.getters.isModalOpen(props.name)
  if (storeIsOpen !== isOpen.value) {
    $modal.toggle(props.name, params.value)
  }
}

const watchModalStore = () => {
  store.watch(
    (state) => state.modal.modals,
    (modals) => {
      isOpen.value = modals?.[props.name]?.isOpen || false
      params.value = modals?.[props.name]?.params || null
    },
    { deep: true }
  )
}

const handleResizer = () => {
  if (isOpen.value) {
    const windowHeight = window.innerHeight
    const modalHeight = document.getElementsByClassName(`vfm__${props.name}`)[0]?.scrollHeight || 0
    let fitFullHeightNewState = false
    if (modalHeight > windowHeight) {
      fitFullHeightNewState = true
    }
    if (fitFullHeight.value !== fitFullHeightNewState) {
      fitFullHeight.value = fitFullHeightNewState
    }
  }
}
</script>

<style></style>
